'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import isClient from 'commons/isClient';
import { buildCategoryPageUrlParams, parseCategoryPageUrlState } from '../categoryPageUrlState';
import { ALL_URL_PARAMS, PAGE_TYPE, getCategoryPageKey } from '../categoryPageTypes';
const getCurrentQueryString = () => isClient() ? window.location.search.replace(/^\?/, '') : '';
const buildPathWithQuery = (pathname, queryStringValue) => {
  const basePath = `${pathname}${pathname.endsWith('/') ? '' : '/'}`;
  return queryStringValue ? `${basePath}?${queryStringValue}` : basePath;
};
const parsePagesParam = pagesParam => {
  const parsedPages = parseInt(pagesParam ?? '0', 10);
  return Number.isFinite(parsedPages) && parsedPages >= 0 ? parsedPages : 0;
};
const getPagesFromSearch = search => {
  const query = search.startsWith('?') ? search.slice(1) : search;
  return parsePagesParam(new URLSearchParams(query).get('pages'));
};

/**
 * Manages URL params for Algolia category pages.
 *
 * Filter/sort updates use `history.replaceState` (not `router.replace`) so
 * changing filters does not trigger a Next.js RSC refetch on every toggle.
 * Reload and back/forward still work because the browser URL is updated.
 */
const useCategoryPageURL = (slug, t0, t1) => {
  const $ = _c(36);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const filters = t2;
  const pageType = t1 === undefined ? PAGE_TYPE.CATEGORY : t1;
  let t3;
  if ($[2] !== pageType || $[3] !== slug) {
    t3 = getCategoryPageKey(pageType, slug);
    $[2] = pageType;
    $[3] = slug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const pageKey = t3;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t4;
  if ($[5] !== searchParams) {
    t4 = () => {
      if (isClient()) {
        return getPagesFromSearch(window.location.search);
      }
      return parsePagesParam(searchParams?.get("pages"));
    };
    $[5] = searchParams;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const [pagesUrlParam, setPagesUrlParam] = useState(t4);
  let t5;
  if ($[7] !== filters || $[8] !== pageType || $[9] !== searchParams) {
    t5 = parseCategoryPageUrlState(searchParams, filters, pageType);
    $[7] = filters;
    $[8] = pageType;
    $[9] = searchParams;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const parsedUrlState = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = nextUrl => {
      if (!isClient()) {
        return;
      }
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (nextUrl === currentUrl) {
        return;
      }
      window.history.replaceState(window.history.state, "", nextUrl);
      const queryStart = nextUrl.indexOf("?");
      const query = queryStart >= 0 ? nextUrl.slice(queryStart) : "";
      setPagesUrlParam(getPagesFromSearch(query));
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const replaceUrlQuery = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!isClient()) {
        return;
      }
      const syncPagesFromLocation = () => {
        setPagesUrlParam(getPagesFromSearch(window.location.search));
      };
      window.addEventListener("popstate", syncPagesFromLocation);
      return () => window.removeEventListener("popstate", syncPagesFromLocation);
    };
    t8 = [];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (!isClient()) {
        return;
      }
      setPagesUrlParam(getPagesFromSearch(window.location.search));
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== searchParams) {
    t10 = [searchParams];
    $[15] = searchParams;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useEffect(t9, t10);
  const prevPageKeyRef = useRef(pageKey);
  let t11;
  let t12;
  if ($[17] !== filters || $[18] !== pageKey || $[19] !== pathname) {
    t11 = () => {
      if (prevPageKeyRef.current === pageKey || !isClient()) {
        return;
      }
      prevPageKeyRef.current = pageKey;
      const currentParams = queryString.parse(window.location.search);
      const filterKeys = new Set([...ALL_URL_PARAMS, ...(filters || []).map(_temp)]);
      const clearedParams = Object.fromEntries(Object.entries(currentParams).filter(t13 => {
        const [key] = t13;
        return !filterKeys.has(key);
      }));
      replaceUrlQuery(buildPathWithQuery(pathname, queryString.stringify(clearedParams)));
    };
    t12 = [pageKey, pathname, filters, replaceUrlQuery];
    $[17] = filters;
    $[18] = pageKey;
    $[19] = pathname;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] !== pathname) {
    t13 = nextParams => {
      const nextQuery = queryString.stringify(nextParams);
      const currentQuery = getCurrentQueryString();
      if (nextQuery === currentQuery) {
        return;
      }
      replaceUrlQuery(buildPathWithQuery(pathname, nextQuery));
    };
    $[22] = pathname;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const replaceUrlParams = t13;
  let t14;
  if ($[24] !== replaceUrlParams) {
    t14 = updates => {
      if (!updates) {
        return;
      }
      const prev = queryString.parse(getCurrentQueryString());
      const merged = {
        ...prev,
        ...updates
      };
      Object.keys(merged).forEach(key_0 => {
        if (merged[key_0] === undefined || merged[key_0] === null) {
          delete merged[key_0];
        }
      });
      replaceUrlParams(merged);
    };
    $[24] = replaceUrlParams;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const updateUrlParamImmediate = t14;
  let t15;
  if ($[26] !== filters || $[27] !== pageType || $[28] !== pagesUrlParam || $[29] !== replaceUrlParams) {
    t15 = (filterState, t16) => {
      const {
        resetPages: t17
      } = t16 === undefined ? {} : t16;
      const resetPages = t17 === undefined ? false : t17;
      const existingParams = queryString.parse(getCurrentQueryString());
      const nextParams_0 = buildCategoryPageUrlParams({
        ...filterState,
        filters,
        pages: resetPages ? 0 : pagesUrlParam,
        existingParams,
        pageType
      });
      if (resetPages) {
        delete nextParams_0.pages;
      }
      replaceUrlParams(nextParams_0);
    };
    $[26] = filters;
    $[27] = pageType;
    $[28] = pagesUrlParam;
    $[29] = replaceUrlParams;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const syncFilterStateToUrl = t15;
  let t16;
  if ($[31] !== pagesUrlParam || $[32] !== parsedUrlState || $[33] !== syncFilterStateToUrl || $[34] !== updateUrlParamImmediate) {
    t16 = {
      pagesUrlParam,
      parsedUrlState,
      updateUrlParamImmediate,
      syncFilterStateToUrl
    };
    $[31] = pagesUrlParam;
    $[32] = parsedUrlState;
    $[33] = syncFilterStateToUrl;
    $[34] = updateUrlParamImmediate;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
};
export default useCategoryPageURL;
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}